import React, {useEffect, useRef, useMemo} from 'react';
import AuthLayout from "../../layouts/AuthLayout";
import ButtonLong from "../../components/ButtonLong";
import TextInput from "../../components/TextInput";
import useAuthStore from "../../store/auth";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import StepPanel from "../../components/registration/StepPanel";
import {useTranslation} from "react-i18next";
import useRegistrationFormStore from "../../store/registration-form";
import Captcha from "../../components/Captcha";
import Button from "../../components/Button";
import {useMutation, useQuery} from "react-query";
import {apollo} from "../../utils/apollo";
import {GET_REGISTRATION_COUNTRIES, GET_REGISTRATION_FORM_FIELDS, REGISTER} from "../../utils/graphql";
import RegistrationStringField from "../../components/registration/fields/RegistrationStringField";
import RegistrationFileField from "../../components/registration/fields/RegistrationFileField";
import RegistrationCountryField from "../../components/registration/fields/RegistrationCountryField";
import RegistrationBooleanField from "../../components/registration/fields/RegistrationBooleanField";
import Loading from "../../components/Loading";
import SelectInput from "../../components/SelectInput";
import {cn} from "../../utils/style";
import {toast} from "react-toastify";
import PasswordStrengthIndicator from "../../components/PasswordStrengthIndicator";

function Register() {
    const {t} = useTranslation();
    const {
        step, setStep, credentials, setCredentials, dynamicFormValue, setDynamicFormValue,
        getCountry
    } = useRegistrationFormStore()
    const recaptcha = useRef()
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {token} = useAuthStore()

    useEffect(() => {
        const refParam = searchParams.get('ref') || searchParams.get('partner');
        if (refParam) {
            const cleanCode = refParam.trim().toLowerCase();
            localStorage.setItem('payd_referral_code', cleanCode);
            setCredentials('referral_code', cleanCode);
        } else {
            const storedRef = localStorage.getItem('payd_referral_code');
            if (storedRef && !credentials.referral_code) {
                setCredentials('referral_code', storedRef);
            }
        }
    }, [searchParams, credentials.referral_code, setCredentials]);

    const fetchCountries = useQuery({
        queryKey: 'registration_countries',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_REGISTRATION_COUNTRIES,
                variables: {},
            });
            return data?.getRegistrationCountries;
        }
    });

    useEffect(() => {
        if (step === 0 && fetchCountries?.data?.length === 1) {
            const singleCountry = fetchCountries.data[0];
            if (singleCountry?.id) {
                setDynamicFormValue('country', singleCountry.id);
                setStep(1);
            }
        }
    }, [step, fetchCountries?.data, setDynamicFormValue, setStep]);

    const fetchRegistrationFormFields = useQuery({
        queryKey: ['registration_form_fields', getCountry()],
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_REGISTRATION_FORM_FIELDS,
                variables: {
                    country: getCountry()
                },
            });
            return data?.getRegistrationForm;
        },
        enabled: !!(getCountry() && step >= 1),
    });

    const fields = useMemo(() => {
        return fetchRegistrationFormFields?.data || null;
    }, [fetchRegistrationFormFields]);

    useEffect(() => {
        if (token) navigate('/dashboard');
    }, [navigate, token]);

    const registerMutation = useMutation({
        mutationFn: async () => {
            const {data} = await apollo.mutate({
                mutation: REGISTER,
                variables: {
                    input: {
                        ...credentials,
                        country: getCountry(),
                        fields: JSON.stringify(dynamicFormValue)
                    }
                },
            });
            return data?.register;
        },
        onError: ((error) => {
            if (error.graphQLErrors) {
                error.graphQLErrors.map((item) => console.log(item));
            }
        }),
        onSuccess: async (data) => {
            if (data) {
                toast.success(t('registration.success_submitted_message') || 'Registration has been submitted successfully, You will get an email soon.');
                setTimeout(() => {
                    window.location.reload();
                }, 5000)
            } else {
                toast.error(t('auth.something_went_wrong'))
            }
        }
    })

    const submitRegistrationForm = async (e) => {
        e.preventDefault();
        const captcha = recaptcha.current?.getValue();
        if (!captcha) {
            alert(t('auth.verify_recaptcha'));
            return;
        }

        registerMutation.mutate();
    }

    return (
        <AuthLayout title={t('auth.register_page_title')}>
            <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-7xl">
                <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">

                    {(step > 0 && fetchRegistrationFormFields.isSuccess && !!fields?.length) && <div className="mt-6">
                        <StepPanel step={step} onClick={value => setStep(value)}/>
                    </div>}

                    {(step > 0 && fetchRegistrationFormFields.isSuccess && !fields?.length) &&
                        <div className="mx-auto max-w-sm">
                            <p className="pb-3">{t('auth.registration_error')}</p>
                            <p className="pb-3">{t('auth.registration_error_help')}</p>
                        </div>}

                    {(fetchRegistrationFormFields.isLoading || fetchCountries.isLoading || (step === 0 && fetchCountries?.data?.length === 1)) && <Loading/>}

                    {fetchCountries?.data && fetchCountries?.data?.length !== 1 && step === 0 && <div className="mx-auto max-w-sm">
                        <SelectInput name="country" label={t('auth.registration_country')} id="country"
                                     get={dynamicFormValue['country'] || ''}
                                     set={v => setDynamicFormValue('country', v)}>
                            <option value=""></option>
                            {fetchCountries?.data?.map((country) => (
                                <option key={country?.id} value={country?.id}>{country?.englishName}</option>))}
                        </SelectInput>
                        <Button className="text-lg w-full max-w-sm mt-6" onClick={() => {
                            if (getCountry()) setStep(step + 1)
                        }}>{t('auth.continue')}</Button>
                    </div>}

                    {!!fields?.length && <div className="mt-6">
                        <div className="mt-10 space-y-8 pb-12 sm:space-y-0 sm:pb-0">
                            {fields?.map(field => {
                                if (field.fieldType === 'string') return <RegistrationStringField key={field.id}
                                                                                                  field={field}
                                                                                                  show={step === field.displayOnSection}
                                                                                                  get={dynamicFormValue}
                                                                                                  set={setDynamicFormValue}/>
                                if (field.fieldType === 'file') return <RegistrationFileField key={field.id}
                                                                                              field={field}
                                                                                              show={step === field.displayOnSection}
                                                                                              get={dynamicFormValue}
                                                                                              set={setDynamicFormValue}/>
                                if (field.fieldType === 'country') return <RegistrationCountryField key={field.id}
                                                                                                    field={field}
                                                                                                    show={step === field.displayOnSection}
                                                                                                    get={getCountry()}
                                                                                                    set={null}
                                                                                                    readOnly={true}
                                                                                                    countries={fetchCountries?.data}/>
                                // if (field.fieldType === 'country') return <RegistrationStringField key={field.id} field={field} show={step === field.displayOnSection} get={dynamicFormValue} set={setDynamicFormValue} defaultValue={registrationCountry} readOnly={true}/>
                                if (field.fieldType === 'boolean') return <RegistrationBooleanField key={field.id}
                                                                                                    field={field}
                                                                                                    show={step === field.displayOnSection}
                                                                                                    get={dynamicFormValue}
                                                                                                    set={setDynamicFormValue}/>
                                return null
                            })}
                        </div>

                        <form className={cn("space-y-6 max-w-[480px] mx-auto", {
                            'hidden': step !== 4
                        })} action="#" method="POST">
                            {credentials.referral_code && (
                                <div className="flex items-center gap-2.5 p-3 bg-cyan-50 border border-cyan-200 rounded-lg text-cyan-800 text-sm">
                                    <span className="flex h-2 w-2 relative">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
                                    </span>
                                    <span>
                                        Partner Referral Applied: <strong className="font-semibold tracking-wide uppercase">{credentials.referral_code}</strong>
                                    </span>
                                </div>
                            )}

                            <TextInput label={t('auth.name')} get={credentials.name} set={v => setCredentials('name', v)}
                                       id="name" name="name" type="text" required={true}/>
                            <TextInput label={t('auth.email_address')} get={credentials.email}
                                       set={v => setCredentials('email', v)} id="email" name="email" type="email"
                                       required={true}/>
                            <TextInput label={t('auth.password')} get={credentials.password}
                                       set={v => setCredentials('password', v)} id="password" name="password"
                                       type="password" required={true}/>
                            <PasswordStrengthIndicator password={credentials.password || ''} />
                            <TextInput label={t('auth.confirm_password')} get={credentials.confirm_password}
                                       set={v => setCredentials('confirm_password', v)} id="confirm_password"
                                       name="confirm_password" type="password" required={true}/>

                            <TextInput label={t('auth.referral_code', 'Partner Referral Code (Optional)')}
                                       get={credentials.referral_code}
                                       set={v => {
                                           const val = v ? v.trim().toLowerCase() : '';
                                           setCredentials('referral_code', val);
                                           if (val) localStorage.setItem('payd_referral_code', val);
                                           else localStorage.removeItem('payd_referral_code');
                                       }}
                                       id="referral_code" name="referral_code" type="text"
                                       placeholder="e.g. uaqftz"/>

                            <div className="flex">
                                <Captcha ref={recaptcha} />
                            </div>
                            <div className={cn({'hidden': step !== 4})}>
                                <ButtonLong type="button" onClick={submitRegistrationForm}>{t('auth.sign_up')}</ButtonLong>
                            </div>
                        </form>

                        <div className={cn("items-center sm:gap-4 sm:py-4", {
                            'flex': step < 4,
                            'hidden': step >= 4
                        })}>
                            <Button className="text-lg w-full max-w-sm"
                                    onClick={() => setStep(step + 1)}>{t('auth.continue')}</Button>
                        </div>
                    </div>}
                </div>

                <p className="mt-10 text-center text-sm text-gray-500">
                    {t('auth.already_signed_up')}{' '}
                    <Link to="/login" className="font-semibold leading-6 text-cyan-500 hover:text-cyan-500">
                        {t('auth.go_to_login')}
                    </Link>
                </p>
            </div>
        </AuthLayout>
    );
}


export default Register;
